import type { AnyElementOption } from "./types/element-options";
import type { AnyElementAction } from "./types/actions";
import type { DeviceVisibility, UserGroupVisibility } from "./types/visibility";
import type { BlockDefinition } from "./types/definition";

export interface Navigation {
    id: string;
    title?: string;
    /**
     * Max depth is 3 levels, e.g.:
     * ```
     * [CATEGORY -> CATEGORY -> LINK]
     * ```
     *
     * Trying to add more levels _will_ result in an error
     */
    items: AnyNavigationItem[];
}

export type AnyNavigationItem = NavigationLink | NavigationCategory;
export type NavigationItemType = "LINK" | "CATEGORY";
interface NavigationItem<T extends NavigationItemType> {
    id: string;
    type: T;
    title: string;
    /**
     * kebab-case name of an untitled-ui icon (https://storybook.softr.design/?path=/docs/icons--docs)
     *
     * @example "code-square-02"
     */
    icon?: string;
    visibility: NavigationVisibility;
    options: Record<string, AnyElementOption>;
    deletable: boolean;
}

export interface NavigationLink extends NavigationItem<"LINK"> {
    action: AnyNavigationAction;
}

export interface NavigationCategory extends NavigationItem<"CATEGORY"> {
    children: AnyNavigationItem[];
}

export type AnyNavigationAction = Extract<
    AnyElementAction,
    { action: "OPEN_URL" | "OPEN_PAGE" | "SIGN_OUT" | "SCROLL_TO" }
>;

export type NavigationVisibility = UserGroupVisibility & DeviceVisibility;

export type NavigationLayoutDesktop = "side" | "top" | "top_and_side";
export type NavigationLayoutMobile = "bottom" | "top" | "top_and_bottom";

export interface NavigationLayout {
    desktop: {
        presets: NavigationPreset<NavigationLayoutDesktop>;
    };
    mobile: {
        presets: NavigationPreset<NavigationLayoutMobile>;
    };
}

export interface NavigationPreset<T extends string> {
    items: NavigationPresetItem<T>[];
    activePreset: T;
}

export interface NavigationPresetItem<T extends string> {
    title: string;
    name: T;
    image: string;
}

/**
 * Checks if a given URL matches a destination URL using normalized comparison.
 * Strips search params from both URLs. Only considers the current URL's hash
 * when the destination itself has a hash.
 *
 * @param currentUrl - The current page URL (absolute)
 * @param destination - The destination URL (can be relative, resolved against origin)
 * @returns true if the URLs match
 */
export function urlMatchesDestination(currentUrl: string, destination: string): boolean {
    let current: URL;
    let dest: URL;

    // The URLs might come from the user, so it might not be a valid URL
    // in that case, the URL constructor will throw an error
    try {
        current = new URL(currentUrl);
        dest = new URL(destination, window.location.origin);
    } catch (e) {
        console.error(`Error parsing URLs, currentUrl="${currentUrl}", destination="${destination}":`, e);
        return false;
    }

    // @TODO: decide whether we should be strict with search params
    // e.g. does `/list?filter-by=foo` match `/list`?
    // and if we do, what do we do with internal search params like `autoUser`, `show-toolbar`, etc.
    current.search = "";
    dest.search = "";

    // Only consider the current URL's hash when the destination itself has a hash.
    // This prevents `/page#section` from failing to match a nav link pointing to `/page`.
    if (!dest.hash) {
        current.hash = "";
    }

    return dest.toString() === current.toString();
}

export function isNavigationLink(item: AnyNavigationItem): item is NavigationLink {
    return item.type === "LINK";
}

export function isNavigationCategory(item: AnyNavigationItem): item is NavigationCategory {
    return item.type === "CATEGORY";
}

export function getNavigationDesktopLayout(definition: BlockDefinition): NavigationLayoutDesktop | null {
    // Early returns:
    // This function is called from the page renderer's app root, so we try not to throw here
    // because that can cause the entire app to crash.
    // Instead, we're logging warnings. Normally, these would be assertions.

    if (definition.name !== "navigation") {
        console.warn("Trying to get navigation layout from a block definition which is not a navigation block");
        return null;
    }
    if (!definition.elements) {
        console.warn("Missing elements in navigation block definition");
        return null;
    }
    if (
        typeof definition.elements.layout !== "object" ||
        definition.elements.layout === null ||
        !("desktop" in definition.elements.layout)
    ) {
        console.warn("Missing layout.desktop in navigation block definition");
        return null;
    }
    const layout = definition.elements.layout as NavigationLayout;
    const activePreset = layout.desktop.presets.activePreset;
    if (!activePreset) {
        console.warn("Navigation block is missing desktop.presets.activePreset in its layout element");
        return null;
    }
    return activePreset;
}
